import { z } from 'zod';
import { isUlid } from '../ulid.js';
import { isPositionCode } from '../position-code.js';
import { DEFAULT_PAGE_SIZE, MAX_PAGE_SIZE } from '../api.js';
import { LOCALES } from '../enums.js';

/**
 * Shared Zod primitives. Every request body in the Worker is validated against
 * a schema built from these, and the resulting field-keyed errors go straight
 * into the API envelope's `errors` object (dev brief §10).
 */

export const ulidSchema = z.string().refine(isUlid, { message: 'Must be a ULID' });

/** Calendar date with no time: `YYYY-MM-DD`, stored as TEXT (§5.2 rule 3). */
export const dateSchema = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/, 'Must be YYYY-MM-DD')
  .refine((s) => {
    const d = new Date(`${s}T00:00:00Z`);
    return !Number.isNaN(d.getTime()) && d.toISOString().slice(0, 10) === s;
  }, 'Not a real calendar date');

/** Instant, ISO-8601 UTC. Display converts to Asia/Vientiane. */
export const timestampSchema = z
  .string()
  .datetime({ offset: false })
  .describe('ISO-8601 UTC');

export const positionCodeSchema = z
  .string()
  .transform((s) => s.trim().toUpperCase())
  .refine(isPositionCode, 'Must be a position code, e.g. SNK-A03-042');

export const localeSchema = z.enum(LOCALES);

/** Money in minor units — never a decimal on the wire (§5.2 rule 7). */
export const minorUnitsSchema = z
  .number()
  .int('Money must be an integer in minor units')
  .safe();

export const cursorSchema = z.string().min(1).max(512).optional();

export const limitSchema = z.coerce
  .number()
  .int()
  .min(1)
  .max(MAX_PAGE_SIZE)
  .default(DEFAULT_PAGE_SIZE);

export const paginationSchema = z.object({
  cursor: cursorSchema,
  limit: limitSchema,
});
export type Pagination = z.infer<typeof paginationSchema>;

/**
 * `client_generated_at` — dev brief §17.3.
 *
 * Distinct from `created_at`: when the farmer recorded it versus when the
 * server received it. v1 is online-only and this is almost always "now", but
 * the column cannot be backfilled once the offline PWA arrives, and the
 * difference is exactly what conflict resolution needs.
 */
export const clientGeneratedAtSchema = timestampSchema.optional();

/**
 * Turn a Zod error into the `{ field: [messages] }` shape the API contract
 * requires. Nested paths flatten with dots so a Vue form can bind directly.
 */
export function toFieldErrors(error: z.ZodError): Record<string, string[]> {
  const out: Record<string, string[]> = {};
  for (const issue of error.issues) {
    const key = issue.path.length ? issue.path.join('.') : '_';
    (out[key] ??= []).push(issue.message);
  }
  return out;
}
